if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.variants = JSON.parse(this.dataset.variants);
        if (document.querySelector('.gift-card__design-thumb')) {
          this.initVariantPicker();
        }
      }

      initVariantPicker() {
        this.styleButtons = this.querySelectorAll('.gift-card__design-thumb');
        this.amountButtons = this.querySelectorAll('.gift-card__amount-button');
        this.previewImages = this.querySelectorAll('.gift-card__media-preview-wrapper');
        this.variantInput = document.getElementById('variant-id');

        this.selectedStyle = this.querySelector('.gift-card__design-thumb.selected')?.dataset.style;
        this.selectedAmount = this.querySelector('.gift-card__amount-button.btn--secondary')?.dataset.amount;

        this.setInitialPreviewImage();
        this.updateVariant();

        this.styleButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.styleButtons.forEach((b) => b.classList.remove('selected'));
            button.classList.add('selected');
            this.selectedStyle = button.dataset.style;

            this.previewImages.forEach((wrapper) => {
              wrapper.classList.remove('is-visible');
              if (wrapper.dataset.style === this.selectedStyle) {
                wrapper.classList.add('is-visible');
              }
            });

            this.updateVariant();
          });
        });

        this.amountButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.amountButtons.forEach((b) => b.classList.remove('btn--secondary'));
            button.classList.add('btn--secondary');
            this.selectedAmount = button.dataset.amount;
            this.updateVariant();
          });
        });
      }

      setInitialPreviewImage() {
        if (!this.selectedStyle) return;

        this.previewImages.forEach((wrapper) => {
          wrapper.classList.remove('is-visible');
          if (wrapper.dataset.style === this.selectedStyle) {
            wrapper.classList.add('is-visible');
          }
        });
      }

      updateVariant() {
        if (!this.selectedStyle || !this.selectedAmount) return;

        const match = this.variants.find((v) => v.option2 === this.selectedStyle && v.option1 === this.selectedAmount);

        if (match) {
          this.variantInput.value = match.id;

          const imageInput = this.form.querySelector('[name="properties[Gift Card Image]"]');
          if (imageInput && match.featured_image?.src) {
            imageInput.value = `${match.featured_image.src}&width=600`;
          }
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const recipientForm = this.querySelector('recipient-form');
        if (recipientForm && !recipientForm.validateForm()) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        window
          .cartFetchWithRetry(`${routes.cart_add_url}`, config)
          .then((response) => {
            // Shopify returns "sold out" as HTTP 422 with a valid JSON body, so 422 must fall
            // through to the parsing below. Any other failing status (429 after the retry, 5xx)
            // answers with HTML — calling .json() on it throws and the shopper would see the
            // button reset as if the add had succeeded.
            if (!response.ok && response.status !== 422) {
              throw new Error(`Cart add failed (${response.status})`);
            }
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            // Reached when the cart request stayed unavailable through the retry, or the
            // connection dropped. Tell the shopper instead of resetting the button silently.
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: e.message,
              message: e.message,
            });
            this.handleErrorMessage(window.cartStrings?.error || e.message);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
